import { useTranslation } from '@mint-lib/i18n';
import { Close, Information } from '@mint-lib/icons';
import {
  Button,
  Divider,
  DrawerDescription,
  DrawerTitle,
  inputVariants,
  Switch,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  TemplateListItem,
  TemplateListItemV1,
  TemplateListItemV2,
} from '../../api/types.js';
import { useLocalizedGranularityLevelLabels } from '../../constants/i18n/granularityLevel.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import {
  usePlatformsCatalog,
  useTemplatePreview,
} from '../../queries/index.js';
import { getDateFormatLabel } from '../../utils/index.js';
import { Input } from '../atoms/Input.jsx';
import { TemplateDrawer } from '../atoms/TemplateDrawer.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';
import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from './TableComponents.jsx';

type TemplatePreviewDrawerProps = {
  template: TemplateListItem | null;
  onDrawerClose: () => void;
};

export const TemplatePreviewDrawer = ({
  template,
  onDrawerClose,
}: TemplatePreviewDrawerProps) => {
  const isOpen = !!template;

  return (
    <TemplateDrawer open={isOpen} onClose={onDrawerClose}>
      {isOpen && (
        <TemplatePreviewDrawerContent
          template={template}
          onDrawerClose={onDrawerClose}
        />
      )}
    </TemplateDrawer>
  );
};

const TemplatePreviewDrawerContent = ({
  template,
  onDrawerClose,
}: {
  template: TemplateListItem;
  onDrawerClose: () => void;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const isImportHubV2 = useImportHubV2();

  const { data: templatePreview, isLoading } = useTemplatePreview(template.id);
  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();

  const attributionLevelLabels =
    granularityLevelLabelByType[template.granularityLevel];

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();

  const granularityLevelLabel = attributionLevelLabels;
  const platformLabel = template.platform
    ? platformsByName[template.platform]?.label
    : '';

  const sortedHeaders = templatePreview?.headers.toSorted(
    (a, b) => a.columnPosition - b.columnPosition,
  );

  return (
    <div className="flex flex-1 flex-col gap-4 p-2">
      <div className="mb-4 flex items-center">
        <Button
          aria-label={t('Close')}
          data-dd-action-name="Close"
          onClick={onDrawerClose}
          icon
          size="small"
          variant="plain"
          className="mr-2 size-6 p-1"
        >
          <Close className="size-4" />
        </Button>
        <DrawerTitle>{t('Template mapping visualization')}</DrawerTitle>
        <DrawerDescription className="sr-only">
          {t('View the header mapping details for this template.')}
        </DrawerDescription>
      </div>
      <Input
        label={t('Template name*')}
        value={template.name}
        infoIconWithTooltip={t(
          'The name given to this mapping during the first file ingestion.',
        )}
        readOnly
      />
      <Input
        label={t('Sheet name')}
        value={template.sheetName ?? t('None')}
        readOnly
      />
      <Divider />
      <div className="flex gap-1">
        <h4 className={typographyVariants({ variant: 'h4' })}>
          {t('Ingestion context')}
        </h4>
        <Tooltip
          asChild
          description={t('Defines how file data is ingested and attributed.')}
        >
          <div className="flex h-full items-center">
            <Information className="fill-icon-secondary" />
          </div>
        </Tooltip>
      </div>
      {isImportHubV2 ? (
        <>
          <Input
            label={t('Media Plan*')}
            value={(template as TemplateListItemV2).mediaPlanName}
            readOnly
            autoComplete="off"
          />
          <Input
            label={t('Activity*')}
            value={(template as TemplateListItemV2).activityName}
            readOnly
            autoComplete="off"
          />
        </>
      ) : (
        <Input
          label={t('Campaign*')}
          value={(template as TemplateListItemV1).campaign}
          readOnly
          autoComplete="off"
        />
      )}
      {template.granularityLevel === 'pricing_item' && (
        <Input
          label={t('Platform*')}
          value={platformLabel}
          readOnly
          autoComplete="off"
        />
      )}
      <Input
        label={t('Data attribution level*')}
        value={granularityLevelLabel}
        readOnly
        autoComplete="off"
      />
      <Divider />
      <h4 className={typographyVariants({ variant: 'h4' })}>
        {t('File import setup')}
      </h4>
      <div className="flex w-full justify-stretch gap-4">
        <div className="flex-initial">
          <Input
            label={t('Date format*')}
            infoIconWithTooltip={t(
              'Date format used to read the dates column in the file.',
            )}
            value={getDateFormatLabel(t, template.dateOrder)}
            readOnly
          />
        </div>
        <div className="flex-initial">
          <Input
            label={t('Number format')}
            infoIconWithTooltip={t(
              'Number format used to interpret the decimal separator in metrics.',
            )}
            value={template.numberFormat}
            readOnly
          />
        </div>
        <div className="ml-auto flex shrink basis-auto flex-col">
          <div className="mb-1 flex items-center gap-2">
            <Switch
              checked={Boolean(templatePreview?.footerRowsCount)}
              disabled
              size="small"
            />
            <span
              className={cn(
                typographyVariants({ variant: 'label01' }),
                'whitespace-nowrap',
              )}
            >
              {t('Remove footer row(s)')}
            </span>
            <Tooltip
              asChild
              description={t(
                'Number of footer rows to be automatically discarded during import.',
              )}
            >
              <div className="flex items-center">
                <Information className="fill-icon-secondary" />
              </div>
            </Tooltip>
          </div>
          <Input value={template.footerRowsCount.toString()} readOnly />
        </div>
      </div>
      <Divider />
      <div>
        <h4 className={cn(typographyVariants({ variant: 'h4' }), 'w-full')}>
          {t('Data mapping')}
        </h4>
        <span
          className={typographyVariants({
            variant: 'label01',
            color: 'secondary',
          })}
        >
          {t('The mapped headers with ARM items from the initial file.')}
        </span>
      </div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-1/2">
                {t('File header')}
              </TableHeaderCell>
              <TableHeaderCell className="w-1/2">
                {t('ARM items')}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 10 }, (_, rowIndex) => (
                  <TableRow key={`template-preview-skeleton-${rowIndex}`}>
                    <TableBodyCell className="w-1/2 py-1" isLoading />
                    <TableBodyCell className="w-1/2 py-1" isLoading />
                  </TableRow>
                ))
              : sortedHeaders?.map(({ rawHeader, mappedHeaderLabel }) => (
                  <TableRow key={rawHeader}>
                    <TableBodyCell className="w-1/2 py-1">
                      <div
                        className={cn(
                          inputVariants({ size: 'medium', color: 'default' }),
                          'items-center justify-start bg-ui-field01 text-text-secondary hover:border-border-primary',
                        )}
                      >
                        {rawHeader}
                      </div>
                    </TableBodyCell>
                    <TableBodyCell className="w-1/2 py-1">
                      <div
                        className={cn(
                          inputVariants({ size: 'medium', color: 'default' }),
                          'items-center justify-start bg-ui-field01 text-text-secondary hover:border-border-primary',
                        )}
                      >
                        {mappedHeaderLabel ?? ''}
                      </div>
                    </TableBodyCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
